import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const Dashboard = () => {
  const navigate = useNavigate();
  const [financings, setFinancings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authorized
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn) {
      navigate('/signin');
    }
  }, [navigate]);

  // Mock data for demonstration
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const mockFinancings = [
        {
          id: 1,
          businessName: 'Tech Startup LLC',
          contactName: 'Ahmed Khan',
          email: 'ahmed@techstartup.com',
          phone: '+1-555-0123',
          financingAmount: '$250,000',
          financingType: 'Murabaha',
          status: 'Pending Review',
          submittedDate: '2024-01-15',
          priority: 'High'
        },
        {
          id: 2,
          businessName: 'Green Energy Solutions',
          contactName: 'Fatima Ali',
          email: 'fatima@greenenergy.com',
          phone: '+1-555-0124',
          financingAmount: '$500,000',
          financingType: 'Musharaka',
          status: 'Under Review',
          submittedDate: '2024-01-14',
          priority: 'High'
        },
        {
          id: 3,
          businessName: 'Halal Foods Inc',
          contactName: 'Omar Hassan',
          email: 'omar@halalfoods.com',
          phone: '+1-555-0125',
          financingAmount: '$100,000',
          financingType: 'Ijara',
          status: 'Approved',
          submittedDate: '2024-01-10',
          priority: 'Medium'
        },
        {
          id: 4,
          businessName: 'Islamic Education Center',
          contactName: 'Aisha Ibrahim',
          email: 'aisha@ieducation.org',
          phone: '+1-555-0126',
          financingAmount: '$75,000',
          financingType: 'Qard Hassan',
          status: 'Pending Documents',
          submittedDate: '2024-01-12',
          priority: 'Low'
        }
      ];
      setFinancings(mockFinancings);
      setIsLoading(false);
    }, 1000);
  }, []);

  // Filter financings based on search and status
  const filteredFinancings = financings.filter(financing => {
    const matchesSearch = financing.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         financing.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         financing.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || financing.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate statistics
  const stats = {
    totalApplications: financings.length,
    pendingReview: financings.filter(f => f.status === 'Pending Review').length,
    underReview: financings.filter(f => f.status === 'Under Review').length,
    approved: financings.filter(f => f.status === 'Approved').length,
    totalAmount: financings.reduce((sum, financing) => {
      const amount = parseInt(financing.financingAmount.replace(/[$,]/g, ''));
      return sum + amount;
    }, 0)
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Under Review':
        return 'bg-blue-100 text-blue-800';
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Pending Documents':
        return 'bg-orange-100 text-orange-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'text-red-600';
      case 'Medium':
        return 'text-yellow-600';
      case 'Low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleViewFinancing = (financing) => {
    // If financing is approved, go to payments page, otherwise go to lifecycle
    if (financing.status === 'Approved') {
      navigate(`/crm/payments/${financing.id}`);
    } else {
      navigate(`/crm/financing/${financing.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Financing Management CRM</h1>
              <p className="text-sm text-gray-600">Halal Financing Applications Dashboard</p>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </Button>
              <Button>
                Add Application
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Applications</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalApplications}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending Review</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.pendingReview}</p>
                </div>
                <div className="bg-yellow-100 p-3 rounded-full">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Under Review</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.underReview}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Amount</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${(stats.totalAmount / 1000000).toFixed(1)}M
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Search by business name, contact, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="flex items-center gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Status</option>
                  <option value="Pending Review">Pending Review</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Approved">Approved</option>
                  <option value="Pending Documents">Pending Documents</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financings Table */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Financing Applications</h2>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                <p className="mt-4 text-gray-600">Loading applications...</p>
              </div>
            ) : filteredFinancings.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No applications found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Financing Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredFinancings.map((financing) => (
                      <tr 
                        key={financing.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewFinancing(financing)}
                      >
                        <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{financing.businessName}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="text-gray-900">{financing.contactName}</div>
                            <div className="text-gray-500">{financing.phone}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{financing.email}</td>
                        <td className="px-4 py-4 whitespace-nowrap font-semibold text-gray-900">{financing.financingAmount}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{financing.financingType}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <Badge className={getStatusColor(financing.status)}>
                            {financing.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${getPriorityColor(financing.priority)}`}>
                            {financing.priority}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{financing.submittedDate}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => handleViewFinancing(financing)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              View
                            </button>
                            <button className="text-green-600 hover:text-green-900">Edit</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;

